// src/components/ClientDetails.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faDollarSign, faWallet, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { formatChileanDate } from '../utils/dateUtils';
import { formatRut, normalizeRut } from '../utils/formatRut';

const DetalleCliente = () => {
    const { currentUser } = useContext(UserContext);
    const { rut } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [clientes, setClientes] = useState([]);
    const [selectedRut, setSelectedRut] = useState(rut || '');
    const [searchTerm, setSearchTerm] = useState('');
    const [facturas, setFacturas] = useState([]);
    const [honorarios, setHonorarios] = useState([]);
    const [resumenFacturas, setResumenFacturas] = useState({
        cantidadFacturas: 0,
        totalMonto: 0,
        totalPagado: 0,
        totalPendiente: 0,
    });
    const [resumenHonorarios, setResumenHonorarios] = useState({
        cantidadHonorarios: 0,
        totalMonto: 0,
        totalPagado: 0,
        totalPendiente: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFacturas, setSelectedFacturas] = useState([]);
    const [selectedHonorarios, setSelectedHonorarios] = useState([]);

    // Fetch all clients for the select dropdown
    useEffect(() => {
        const fetchClientes = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await api.get('/clientes', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                //console.log('Clientes:', response.data); // Añadido para depuración
                setClientes(response.data);
            } catch (err) {
                console.error('Error al obtener clientes:', err);
                setError('Error al obtener clientes.');
            }
        };

        fetchClientes();
    }, []);

    // Fetch facturas and honorarios when a client is selected
    useEffect(() => {
        if (selectedRut) {
            fetchData();
        }
        // eslint-disable-next-line
    }, [selectedRut]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');

            // Fetch Facturas
            const responseFacturas = await api.get(
                `/facturas/rut/${selectedRut}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            //console.log('Facturas:', responseFacturas.data); // Añadido para depuración
            //setFacturas(Array.isArray(responseFacturas.data) ? responseFacturas.data : []);

            // Ordenar facturas por número
            const facturasOrdenadas = Array.isArray(responseFacturas.data)
                ? responseFacturas.data.sort((a, b) => a.numero - b.numero)
                : [];
            setFacturas(facturasOrdenadas);

            // Fetch Honorarios
            const responseHonorarios = await api.get(
                `/honorarios/rut/${selectedRut}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            //console.log('Honorarios:', responseHonorarios.data); // Añadido para depuración
            const honorariosOrdenados = Array.isArray(responseHonorarios.data)
                ? responseHonorarios.data.sort((a, b) => a.descripcion.localeCompare(b.descripcion))
                : [];
            setHonorarios(honorariosOrdenados);

            // Calcular Resumen Facturas
            const totalMonto = Array.isArray(responseFacturas.data)
                ? responseFacturas.data.reduce((acc, factura) => acc + factura.monto, 0)
                : 0;
            const totalPagado = Array.isArray(responseFacturas.data)
                ? responseFacturas.data.reduce((acc, factura) => acc + (factura.total_abonado || 0), 0)
                : 0;
            const totalPendiente = totalMonto - totalPagado;
            setResumenFacturas({
                cantidadFacturas: Array.isArray(responseFacturas.data) ? responseFacturas.data.length : 0,
                totalMonto,
                totalPagado,
                totalPendiente,
            });

            // Calcular Resumen Honorarios
            const totalMontoHonorarios = Array.isArray(responseHonorarios.data)
                ? responseHonorarios.data.reduce((acc, honorario) => acc + honorario.monto, 0)
                : 0;
            const totalPagadoHonorarios = Array.isArray(responseHonorarios.data)
                ? responseHonorarios.data.reduce((acc, honorario) => acc + (honorario.total_abonado || 0), 0)
                : 0;
            const totalPendienteHonorarios = totalMontoHonorarios - totalPagadoHonorarios;
            setResumenHonorarios({
                cantidadHonorarios: Array.isArray(responseHonorarios.data) ? responseHonorarios.data.length : 0,
                totalMonto: totalMontoHonorarios,
                totalPagado: totalPagadoHonorarios,
                totalPendiente: totalPendienteHonorarios,
            });
        } catch (err) {
            console.error('Error al obtener datos:', err);
            // Verifica si el error está relacionado con la falta de documentos
            // Esto depende de cómo el backend maneja estos casos
            // Por ejemplo, si retorna un 404 cuando no hay documentos, podrías manejarlo aquí
            if (err.response && err.response.status === 404) {
                // No se encontraron documentos para el cliente
                setFacturas([]);
                setHonorarios([]);
                setResumenFacturas({
                    cantidadFacturas: 0,
                    totalMonto: 0,
                    totalPagado: 0,
                    totalPendiente: 0,
                });
                setResumenHonorarios({
                    cantidadHonorarios: 0,
                    totalMonto: 0,
                    totalPagado: 0,
                    totalPendiente: 0,
                });
            } else {
                // Otro tipo de error
                setError('Error al obtener datos del cliente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSelectChange = (e) => {
        setSelectedRut(e.target.value);
    };

    const filteredClientes = Array.isArray(clientes)
        ? clientes.filter((cliente) => {
            const term = searchTerm.toLowerCase();
            const normalizedTerm = normalizeRut(searchTerm);
            const normalizedRut = normalizeRut(cliente.rut);
            return (
                cliente.nombre.toLowerCase().includes(term) ||
                normalizedRut.includes(normalizedTerm)
            );
        })
        : [];



    const filteredFacturas = facturas.filter(factura => {
        //console.log('Facturas:', facturas); // Depuración
        const clienteNombre = clientes[factura.clienteRut] || '';

        return (
            factura.numero.toString().includes(searchTerm) ||
            clienteNombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            factura.clienteRut.includes(searchTerm)
        );
    })


    const handleEditFactura = (factura) => {
        navigate(`/facturas/editar/${factura._id}`, {
            state: {
                from: 'DetalleCliente',
                rutCliente: selectedRut, // Pasa el RUT del cliente
            },
        });
    };


    const handleAbonarFactura = (factura) => {
        navigate(`/abonos${location.search}`, { state: { factura, from: 'DetalleCliente', rutCliente: selectedRut } })
    };


    const handleEditarHonorario = (honorario) => {
        navigate(`/honorarios/editar/${honorario._id}`, {
            state: {
                from: 'DetalleCliente',
                rutCliente: selectedRut,
            }
        });
    };

    const handleAbonarHonorario = (honorario) => {
        navigate(`/abonosHonorarios/${honorario._id}`, {
            state: {
                from: 'DetalleCliente',
                rutCliente: selectedRut,
            }
        });
    };


    // Función para seleccionar/deseleccionar una factura
    const handleSelectFactura = (id) => {
        if (selectedFacturas.includes(id)) {
            setSelectedFacturas(selectedFacturas.filter((facturaId) => facturaId !== id));
        } else {
            setSelectedFacturas([...selectedFacturas, id]);
        }
    };

    // Función para seleccionar/deseleccionar todas las facturas
    const handleSelectAll = () => {
        if (selectedFacturas.length === facturas.length) {
            setSelectedFacturas([]);
        } else {
            setSelectedFacturas(facturas.map((factura) => factura._id));
        }
    };

    // Variable para controlar el estado del checkbox "Seleccionar Todo"
    const isAllSelected = selectedFacturas.length === facturas.length && facturas.length > 0;


    // Seleccionar/Deseleccionar un honorario
    const handleSelectHonorario = (id) => {
        setSelectedHonorarios((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter((honorarioId) => honorarioId !== id)
                : [...prevSelected, id]
        );
    };

    // Seleccionar/Deseleccionar todos los honorarios
    const handleSelectAllHonorarios = () => {
        if (isAllHonorariosSelected) {
            setSelectedHonorarios([]);
        } else {
            const allHonorarioIds = honorarios.map((honorario) => honorario._id);
            setSelectedHonorarios(allHonorarioIds);
        }
    };

    // Verificar si todos los honorarios están seleccionados
    const isAllHonorariosSelected = selectedHonorarios.length === honorarios.length && honorarios.length > 0;


    const handlePagoMasivoHonorarios = async () => {
        if (selectedHonorarios.length === 0) {
            Swal.fire('No hay honorarios seleccionados', 'Por favor, selecciona al menos un honorario para pagar.', 'warning');
            return;
        }

        const confirm = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a pagar ${selectedHonorarios.length} honorario(s).`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, pagar',
            cancelButtonText: 'Cancelar',
        });

        if (confirm.isConfirmed) {
            // Agregar logging para verificar los IDs seleccionados
            console.log('Selected Honorarios IDs:', selectedHonorarios);

            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                console.log('Enviando honorarioIds:', selectedHonorarios);

                const response = await api.put(
                    '/honorarios/pagar-masivo',
                    { honorarioIds: selectedHonorarios },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                // Manejar la respuesta exitosa
                console.log(response.data.message);
                Swal.fire('Éxito', response.data.message, 'success');


                fetchData(); // Función para refrescar los datos
                setSelectedHonorarios([]);
            } catch (error) {
                console.error('Error al pagar masivamente honorarios:', error);
                if (error.response && error.response.data && error.response.data.error) {
                    Swal.fire('Error', `Error: ${error.response.data.error}`, 'error');
                } else {
                    Swal.fire('Error', 'Ocurrió un error al pagar masivamente honorarios.', 'error');
                }
            } finally {
                setLoading(false);
            }
        }
    };


    const handleDeleteSelected = async () => {
        if (selectedFacturas.length === 0) return;

        const confirm = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Se eliminarán las facturas seleccionadas.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
        });

        if (confirm.isConfirmed) {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                await api.delete('/facturas/bulk-delete', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    data: { ids: selectedFacturas },
                });
                Swal.fire('Eliminado', 'Facturas eliminadas correctamente.', 'success');
                setSelectedFacturas([]);
                // Refrescar la lista de facturas
                fetchData(selectedRut);
            } catch (error) {
                console.error('Error al eliminar facturas:', error);
                Swal.fire('Error', 'No se pudieron eliminar las facturas.', 'error');
            } finally {
                setLoading(false);
            }
        }
    };


    // Función para manejar la eliminación masiva de honorarios
    const handleEliminarHonorariosMasivo = async () => {
        if (selectedHonorarios.length === 0) {
            Swal.fire('No hay honorarios seleccionados', 'Por favor, selecciona al menos un honorario para eliminar.', 'warning');
            return;
        }

        const confirm = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a eliminar ${selectedHonorarios.length} honorario(s). Esta acción es irreversible.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });

        if (confirm.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');

                const response = await api.delete('/honorarios/eliminar-masivo', {
                    data: { honorarioIds: selectedHonorarios },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                Swal.fire('Eliminado', response.data.message, 'success');
                fetchData(); // Función para refrescar los datos
                setSelectedHonorarios([]);
            } catch (error) {
                console.error('Error al eliminar masivamente honorarios:', error);
                Swal.fire('Error', 'Hubo un error al eliminar los honorarios seleccionados.', 'error');
            } finally {
                setLoading(false);
            }
        }
    };




    // Función para manejar el pago masivo de facturas
    const handlePagoMasivo = async () => {
        if (selectedFacturas.length === 0) {
            Swal.fire('No hay facturas seleccionadas', 'Por favor, selecciona al menos una factura para pagar.', 'warning');
            return;
        }

        const confirm = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a pagar ${selectedFacturas.length} factura(s).`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, pagar',
            cancelButtonText: 'Cancelar',
        });

        if (confirm.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');

                const response = await api.put(
                    '/facturas/pagar-masivo',
                    { facturaIds: selectedFacturas },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                Swal.fire('Éxito', response.data.message, 'success');
                fetchData(); // Función para refrescar los datos
                setSelectedFacturas([]);
            } catch (error) {
                console.error('Error al pagar masivamente facturas:', error);
                Swal.fire('Error', 'Hubo un error al pagar las facturas seleccionadas.', 'error');
            } finally {
                setLoading(false);
            }
        }
    };




    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <h1 className="text-3xl font-semibold mb-6 text-center">Detalles del Cliente</h1>
            {/* Selección de Cliente */}
            <div className="mb-6">
                <select
                    value={selectedRut}
                    onChange={handleSelectChange}
                    className="border border-gray-300 p-3 rounded w-full text-lg focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-md"
                >
                    <option value="">-- Seleccionar Cliente --</option>
                    {filteredClientes.map((cliente) => (
                        <option key={cliente._id} value={cliente.rut}>
                            {cliente.nombre} ({formatRut(cliente.rut)})
                        </option>
                    ))}
                </select>
            </div>

            {/* Select Cliente con Buscador */}

            {/* <label className="block text-gray-700 mb-2 text-lg font-medium">Seleccionar Cliente:</label> */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Buscar por nombre o RUT"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-gray-300 p-3 pl-12 rounded-full w-full focus:ring-2 focus:ring-blue-500 focus:outline-none text-lg shadow-md"
                />
                <span className="absolute left-3 top-2">
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-xl" />
                </span>
            </div>



            {/* Mostrar detalles si un cliente está seleccionado */}
            {selectedRut && (
                <>
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center mb-4 text-xl">{error}</div>
                    ) : (
                        <>
                            {/* Verificar si no hay facturas y honorarios */}
                            {facturas.length === 0 && honorarios.length === 0 ? (
                                <div className="text-center text-gray-700 text-xl">
                                    Cliente sin documentos emitidos.
                                </div>
                            ) : (
                                <>
                                    {/* Resumen Facturas y Honorarios */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                        {/* Resumen Facturas */}
                                        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-500">
                                            <h2 className="text-2xl font-semibold mb-4 text-gray-800 text-center flex items-center justify-center">
                                                <span className="text-blue-500 mr-2">📄</span> Resumen Facturas
                                            </h2>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Cantidad de Facturas:</span>
                                                    <span className="font-medium text-gray-800">{resumenFacturas.cantidadFacturas}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Monto Facturado:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenFacturas.totalMonto.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Abonado:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenFacturas.totalPagado.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Pendiente:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenFacturas.totalPendiente.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Resumen Honorarios */}
                                        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-green-500">
                                            <h2 className="text-2xl font-semibold mb-4 text-gray-800 text-center flex items-center justify-center">
                                                <span className="text-green-500 mr-2">💼</span> Resumen Honorarios
                                            </h2>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Cantidad de Honorarios:</span>
                                                    <span className="font-medium text-gray-800">{resumenHonorarios.cantidadHonorarios}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Monto Facturado:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenHonorarios.totalMonto.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Abonado:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenHonorarios.totalPagado.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Pendiente:</span>
                                                    <span className="font-medium text-gray-800">
                                                        ${resumenHonorarios.totalPendiente.toLocaleString('es-CL')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Listado de Facturas */}
                                    <div className=" mb-12">
                                        <h2 className="text-2xl font-semibold mb-4 text-blue-600 text-center flex items-center justify-center">
                                            <span className="text-blue-500 mr-2">📄</span> Facturas
                                        </h2>

                                        {/* Botones de Acción Facturas */}
                                        <div className="flex space-x-4 mb-6">
                                            {/* Botón Crear Factura */}
                                            <button
                                                onClick={() =>
                                                    navigate('/facturas/nueva', {
                                                        state: {
                                                            from: 'DetalleCliente',
                                                            selectedRut
                                                        },
                                                    })
                                                }
                                                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition duration-200 relative"
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                                Crear Factura
                                            </button>
                                            {/* Botón para Pago Masivo */}
                                            <button
                                                onClick={handlePagoMasivo}
                                                disabled={selectedFacturas.length === 0 || loading}
                                                className={`flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition duration-200 relative ${selectedFacturas.length === 0 || loading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:bg-green-600'
                                                    }`}
                                            >
                                                <FontAwesomeIcon icon={faWallet} className="mr-2" />
                                                Pagar
                                                {selectedFacturas.length > 0 && (
                                                    <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                                        {selectedFacturas.length}
                                                    </span>
                                                )}
                                            </button>

                                            {/* Botón Eliminar Seleccionadas */}
                                            <button
                                                onClick={handleDeleteSelected}
                                                disabled={selectedFacturas.length === 0 || loading}
                                                className={`flex items-center px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200 relative ${selectedFacturas.length === 0 || loading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:bg-red-600'
                                                    }`}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                Eliminar
                                                {selectedFacturas.length > 0 && (
                                                    <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                                        {selectedFacturas.length}
                                                    </span>
                                                )}
                                            </button>

                                        </div>
                                        {/* Botones de Acción Facturas */}

                                        {/* Tabla de Facturas */}
                                        <div className="overflow-x-auto mb-8">
                                            <table className="min-w-full bg-white">
                                                <thead>
                                                    <tr className="bg-blue-500 text-white uppercase text-sm leading-normal">
                                                        <th className="py-3 px-6 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isAllSelected}
                                                                onChange={handleSelectAll}
                                                                className="form-checkbox h-4 w-4 text-blue-600"
                                                            />
                                                        </th>
                                                        <th className="py-3 px-6 text-left">Número</th>
                                                        <th className="py-3 px-6 text-left">Fecha Emisión</th>
                                                        <th className="py-3 px-6 text-left">Fecha Pago</th>
                                                        <th className="py-3 px-6 text-left">Monto</th>
                                                        <th className="py-3 px-6 text-left">Total Abonado</th>
                                                        <th className="py-3 px-6 text-left">Total Adeudado</th>
                                                        <th className="py-3 px-6 text-left">Estado</th>
                                                        <th className="py-3 px-6 text-left">Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="text-gray-600 text-sm">
                                                    {facturas.length > 0 ? (
                                                        facturas.map((factura) => (
                                                            <tr key={factura._id} className="border-b hover:bg-gray-100">
                                                                {/* Checkbox de Selección Individual */}
                                                                <td className="py-3 px-6 text-center border">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedFacturas.includes(factura._id)}
                                                                        onChange={() => handleSelectFactura(factura._id)}
                                                                        className="form-checkbox h-4 w-4 text-blue-600"
                                                                    />
                                                                </td>
                                                                <td className="py-3 px-6 text-left border">{factura.numero}</td>
                                                                <td className="py-3 px-6 text-left border">
                                                                    {formatChileanDate(factura.fechaEmision)}
                                                                </td>
                                                                <td className="py-3 px-6 text-left border">
                                                                    {factura.fechaPago ? formatChileanDate(factura.fechaPago) : 'N/D'}
                                                                </td>
                                                                <td className="py-3 px-6 text-left border">
                                                                    ${factura.monto.toLocaleString('es-CL')}
                                                                </td>
                                                                <td className="py-3 px-6 text-left border">{factura.total_abonado.toLocaleString('es-CL')}</td> {/* Total Abonado */}
                                                                <td className="py-3 px-6 text-left border">{(factura.monto - factura.total_abonado).toLocaleString('es-CL')}</td> {/* Total Adeudado */}
                                                                <td className="py-3 px-6 text-left border">
                                                                    <span className={`py-1 px-3 rounded-full text-xs ${factura.estado === 'pagada' ? 'bg-green-200 text-green-600' : factura.estado === 'abonada' ? 'bg-blue-200 text-blue-600' : 'bg-yellow-200 text-yellow-600'}`}>
                                                                        {factura.estado}
                                                                    </span>
                                                                </td>
                                                                <td className="py-3 px-6 text-left border">
                                                                    <div className="flex space-x-2">
                                                                        <button
                                                                            onClick={() => handleEditFactura(factura)}
                                                                            aria-label="Editar factura" className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-600"
                                                                        >
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                        </button>
                                                                        {/* <Link to={{
                                                                            pathname: `/facturas/editar/${factura._id}`,
                                                                            search: location.search, // Pasa los parámetros de búsqueda aquí
                                                                            state: { from: 'DetalleCliente' },
                                                                        }} aria-label="Editar factura" className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-600">
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                        </Link> */}

                                                                        <button
                                                                            onClick={() => handleAbonarFactura(factura)} aria-label="Abonar factura"
                                                                            className={`bg-yellow-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-yellow-600 ${factura.estado === 'pagada' ? 'opacity-50 cursor-not-allowed' : ''
                                                                                }`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faDollarSign} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="py-3 px-6 text-center">
                                                                No hay facturas disponibles.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Listado de Honorarios */}
                                    <div className="mb-12">
                                        <h2 className="text-2xl font-semibold mb-4 text-green-600 text-center flex items-center justify-center">
                                            <span className="text-green-500 mr-2">💼</span> Honorarios
                                        </h2>

                                        {/* Botones de Acción para Honorarios */}
                                        <div className="flex space-x-4 mb-6">
                                            <button
                                                onClick={handlePagoMasivoHonorarios}
                                                disabled={selectedHonorarios.length === 0 || loading}
                                                className={`flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition duration-200 relative ${selectedHonorarios.length === 0 || loading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:bg-green-600'
                                                    }`}
                                            >
                                                <FontAwesomeIcon icon={faWallet} className="mr-2" />
                                                Pagar
                                                {selectedHonorarios.length > 0 && (
                                                    <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                                        {selectedHonorarios.length}
                                                    </span>
                                                )}
                                            </button>
                                            <button
                                                onClick={handleEliminarHonorariosMasivo}
                                                disabled={selectedHonorarios.length === 0 || loading}
                                                className={`flex items-center px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200 relative ${selectedHonorarios.length === 0 || loading
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : 'hover:bg-red-600'
                                                    }`}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                Eliminar
                                                {selectedHonorarios.length > 0 && (
                                                    <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                                        {selectedHonorarios.length}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                        {/* Botones de Acción para Honorarios */}
                                        <table className="min-w-full bg-white">
                                            <thead>
                                                <tr className="bg-green-500 text-white uppercase text-sm leading-normal">
                                                    <th className="py-2 px-4 border">
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllHonorariosSelected}
                                                            onChange={handleSelectAllHonorarios}
                                                        />
                                                    </th>
                                                    <th className="py-2 px-4 border">Fecha Emisión</th>
                                                    <th className="py-2 px-4 border">Fecha Pago</th>
                                                    <th className="py-2 px-4 border">Monto</th>
                                                    <th className="py-2 px-4 border">Descripción</th> {/* Nueva columna */}
                                                    <th className="py-2 px-4 border">Estado</th>
                                                    <th className="py-2 px-4 border">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 text-sm">
                                                {honorarios.length > 0 ? (
                                                    honorarios.map((honorario) => (
                                                        <tr key={honorario._id} className="border-b even:bg-gray-50 hover:bg-gray-100">
                                                            <td className="py-2 px-4 border">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedHonorarios.includes(honorario._id)}
                                                                    onChange={() => handleSelectHonorario(honorario._id)}
                                                                />
                                                            </td>
                                                            <td className="py-3 px-6 text-left border">
                                                                {formatChileanDate(honorario.fechaEmision)}
                                                            </td>
                                                            <td className="py-3 px-6 text-left border">
                                                                {honorario.fechaPago ? formatChileanDate(honorario.fechaPago) : 'N/D'}
                                                            </td>
                                                            <td className="py-3 px-6 text-left border">
                                                                ${honorario.monto.toLocaleString('es-CL')}
                                                            </td>
                                                            <td className="py-2 px-4 border">{honorario.descripcion}</td> {/* Mostrar 'descripcion' */}
                                                            <td className="py-3 px-6 text-left border">
                                                                <span className={`py-1 px-3 rounded-full text-xs border ${honorario.estado === 'pagada' ? 'bg-green-200 text-green-600' :
                                                                    honorario.estado === 'abonada' ? 'bg-blue-200 text-blue-600' :
                                                                        'bg-yellow-200 text-yellow-600'
                                                                    }`}>
                                                                    {honorario.estado}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-6 text-left border">
                                                                <div className="flex space-x-2">
                                                                    <button onClick={() => handleEditarHonorario(honorario)} aria-label="Editar honorario" className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-600">
                                                                        <FontAwesomeIcon icon={faEdit} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleAbonarHonorario(honorario)}
                                                                        className={`bg-yellow-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-yellow-600 ${honorario.estado === 'pagada' ? 'opacity-50 cursor-not-allowed' : ''}`}

                                                                    >
                                                                        <FontAwesomeIcon icon={faDollarSign} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="py-3 px-6 text-center">
                                                            No hay honorarios disponibles.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
export default DetalleCliente;